import React, { useEffect, useRef, useState } from 'react';
import API, {
  resolvePendingReauth,
  rejectPendingReauth,
  hardLogoutToLogin,
} from './api';

const SessionReauthModal = () => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const onOpen = () => {
      setPassword('');
      setError('');
      setLoading(false);
      setOpen(true);

      setTimeout(() => {
        inputRef.current?.focus?.();
      }, 50);
    };

    window.addEventListener('visitoms:reauth-required', onOpen);
    return () => window.removeEventListener('visitoms:reauth-required', onOpen);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password.trim()) {
      setError('Password is required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await API.post('/auth/reauth', {
        password: password.trim(),
      });

      setOpen(false);
      setPassword('');
      resolvePendingReauth(true);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Re-authentication failed.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await API.post('/logout');
    } catch {
      // best effort
    }

    setOpen(false);
    setPassword('');
    rejectPendingReauth(new Error('User cancelled re-authentication'));
    hardLogoutToLogin(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/50 flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600" />

        <div className="p-6">
          <h2 className="text-xl font-extrabold text-slate-900">
            Session locked
          </h2>

          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            For security reasons, your session was locked after inactivity.
            Re-enter your password to continue.
          </p>

          <form onSubmit={handleSubmit} className="mt-5">
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Password
            </label>

            <input
              ref={inputRef}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full p-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500"
              placeholder="••••••••"
            />

            {error && (
              <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2">
                <p className="text-rose-700 text-sm font-semibold">Re-auth failed</p>
                <p className="text-rose-700 text-xs mt-0.5">{error}</p>
              </div>
            )}

            <div className="mt-5 flex items-center gap-3">
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 py-3 rounded-2xl font-extrabold transition ${
                  loading
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-sky-600 hover:bg-sky-700 text-white'
                }`}
              >
                {loading ? 'Checking…' : 'Continue'}
              </button>

              <button
                type="button"
                onClick={handleLogout}
                className="px-4 py-3 rounded-2xl border border-slate-200 bg-white text-slate-700 font-bold hover:bg-slate-50"
              >
                Log out
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SessionReauthModal;
